import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const refreshUser = useCallback(async () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) return null;
    
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Error refreshing user:', error);
      return null;
    }
  }, []);

  const validateToken = useCallback(async (storedToken) => {
    if (!storedToken) return false;
    
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      });
      setUser(response.data);
      setToken(storedToken);
      return true;
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      }
      return false;
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        await validateToken(storedToken);
      }
      setLoading(false);
    };
    initAuth();
  }, [validateToken]);

  useEffect(() => {
    if (!token) return;
    
    const interval = setInterval(() => {
      validateToken(token);
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [token, validateToken]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const signup = async (email, password, name) => {
    const response = await axios.post(`${API_URL}/api/auth/signup`, { email, password, name });
    // DON'T auto-login after signup - user must verify email first
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const completeOnboarding = async () => {
    await axios.post(`${API_URL}/api/auth/complete-onboarding`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser(prev => ({ ...prev, onboarding_completed: true }));
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      signup,
      logout,
      completeOnboarding,
      refreshUser,
      updateUser,
      getAuthHeader
    }}>
      {children}
    </AuthContext.Provider>
  );
};
